// ---------------------------------------------------------------------------
// DnD Email Editor – Inspector Panel
//
// Dynamic property editor that changes fields based on the selected layer
// or element type.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  MousePointer,
  AlertTriangle,
  ArrowUp,
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  Link2,
  Link2Off,
  Braces,
  Lock,
  Unlock,
  Code2,
  ImageIcon,
  Pencil,
} from 'lucide-react';
import type {
  DndEmailModel,
  Layer,
  Element,
  Column,
  Spacing,
  TextLayer,
  ImageLayer,
  ButtonLayer,
  DividerLayer,
  SpacerLayer,
  HtmlLayer,
  ColumnsLayer,
  SocialLayer,
  SocialLink,
  SocialLogos,
  VideoLayer,
  CountdownLayer,
  BorderSides,
  BorderSide,
} from './dnd-model';
import type { DndAlign } from './dnd-model';
import type { GlobalStyles } from './dnd-model';
import { findLayerById, findElementById } from './dnd-model';
import type { SelectionState } from './useDndEditor';
import { changeColumnsLayout } from './dnd-columns';
import { ColumnLayoutPicker } from './dnd-column-layouts';
import { ColorField } from './ColorField';
import { ColorInput } from './dnd-color-presets';
import { SOCIAL_PLATFORM_VARIABLES, logosFromSet, defaultBorderSides } from './dnd-defaults';
import { WEB_FONTS } from './dnd-webfonts';
import { useSocialLogoSets } from '@/hooks/useSocialLogoSets';

// ---- Reusable input components --------------------------------------------

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  onBrowseImage,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  /** When set, an image-picker button is rendered to the right of the input. */
  onBrowseImage?: () => void;
}) {
  const input = (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={onBrowseImage ? { flex: 1 } : undefined}
    />
  );
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      {onBrowseImage ? (
        <div style={{ display: 'flex', gap: 4 }}>
          {input}
          <button
            type="button"
            className="dnd-inspector-browse-btn icon-only"
            onClick={onBrowseImage}
            title="Choose a photo"
          >
            <ImageIcon size={14} />
          </button>
        </div>
      ) : (
        input
      )}
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min,
  max,
  suffix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <div className="dnd-inspector-field">
      <label>
        {label}
        {suffix && <span className="dnd-suffix">{suffix}</span>}
      </label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
      />
    </div>
  );
}

/**
 * Line-height control storing a px string (e.g. "24px"); empty = inherit.
 * px is used because Outlook desktop only honours line-height reliably in px
 * (paired with mso-line-height-rule:exactly in the compiler).
 */
function LineHeightInput({
  label,
  value,
  onChange,
  placeholder = 'Inherit',
  helpText,
}: {
  label: string;
  value: string | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  helpText?: string;
}) {
  const num = value ? value.replace(/px$/, '') : '';
  return (
    <div className="dnd-inspector-field">
      <label>
        {label}
        <span className="dnd-suffix">px</span>
      </label>
      <input
        type="number"
        min={0}
        value={num}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value.trim();
          onChange(raw === '' ? '' : `${Math.max(0, Number(raw))}px`);
        }}
      />
      {helpText && <p className="dnd-inspector-hint">{helpText}</p>}
    </div>
  );
}

function SelectInput({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckboxInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="dnd-inspector-field dnd-inspector-checkbox">
      <label>
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
    </div>
  );
}

const SPACING_SIDES: {
  key: keyof Spacing;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'top', label: 'Top', icon: <ArrowUp size={13} /> },
  { key: 'right', label: 'Right', icon: <ArrowRight size={13} /> },
  { key: 'bottom', label: 'Bottom', icon: <ArrowDown size={13} /> },
  { key: 'left', label: 'Left', icon: <ArrowLeft size={13} /> },
];

function isUniform(v: Spacing) {
  return v.top === v.right && v.right === v.bottom && v.bottom === v.left;
}

function SpacingInput({
  label,
  value,
  onChange,
  helpText = 'Space inside the block, between its edge and its content.',
}: {
  label: string;
  value: Spacing;
  onChange: (v: Spacing) => void;
  helpText?: string;
}) {
  // When "linked" is on, editing any side fills all four with the same value.
  // Default it on when the block already has even spacing on every side.
  const [linked, setLinked] = useState(() => isUniform(value));

  const clamp = (n: number) => (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);

  const setSide = (side: keyof Spacing, raw: number) => {
    const n = clamp(raw);
    if (linked) onChange({ top: n, right: n, bottom: n, left: n });
    else onChange({ ...value, [side]: n });
  };

  return (
    <div className="dnd-inspector-field dnd-spacing">
      <div className="dnd-spacing-header">
        <label>{label}</label>
        <button
          type="button"
          className={`dnd-spacing-link${linked ? ' active' : ''}`}
          onClick={() => setLinked((v) => !v)}
          aria-pressed={linked}
          title={
            linked
              ? 'Linked: one value applies to all four sides. Click to unlink.'
              : 'Unlinked: each side is set on its own. Click to link all sides.'
          }
        >
          {linked ? <Link2 size={12} /> : <Link2Off size={12} />}
          <span>{linked ? 'Linked' : 'Unlinked'}</span>
        </button>
      </div>

      <div className={`dnd-spacing-sides${linked ? ' is-linked' : ''}`}>
        {SPACING_SIDES.map((s) => (
          <label key={s.key} className="dnd-spacing-side">
            <span className="dnd-spacing-side-head">
              <span className="dnd-spacing-side-icon" aria-hidden="true">
                {s.icon}
              </span>
              {s.label}
            </span>
            <span className="dnd-spacing-side-input">
              <input
                type="number"
                min={0}
                value={value[s.key]}
                onChange={(e) => setSide(s.key, Number(e.target.value))}
                aria-label={`${s.label} ${label.toLowerCase()} in pixels`}
              />
              <span className="dnd-spacing-unit">px</span>
            </span>
          </label>
        ))}
      </div>

      {helpText && <p className="dnd-spacing-help">{helpText}</p>}
    </div>
  );
}

function AlignInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DndAlign;
  onChange: (v: DndAlign) => void;
}) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <div className="dnd-align-group">
        {(['left', 'center', 'right'] as const).map((a) => (
          <button
            key={a}
            className={value === a ? 'active' : ''}
            onClick={() => onChange(a)}
            title={a.charAt(0).toUpperCase() + a.slice(1)}
          >
            {a === 'left' ? <AlignLeft size={12} /> : a === 'center' ? <AlignCenter size={12} /> : <AlignRight size={12} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <div className="dnd-inspector-section-header">{title}</div>;
}

// ---- Font family options --------------------------------------------------

const FONT_OPTIONS = [
  // See DndGlobalStyles: web fonts lead, each with a web-safe fallback stack.
  ...WEB_FONTS.map((f) => ({ value: f.stack, label: f.label })),
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, Times, serif', label: 'Georgia' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Courier New, Courier, monospace', label: 'Courier New' },
  { value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
  { value: 'Lucida Console, Monaco, monospace', label: 'Lucida Console' },
];

// ---- Per-type inspector panels --------------------------------------------

function TextLayerInspector({
  layer,
  onChange,
  onEdit,
}: {
  layer: TextLayer;
  onChange: (patch: Partial<TextLayer>) => void;
  onEdit?: () => void;
}) {
  return (
    <>
      <SectionHeader title="Text" />
      <AlignInput
        label="Text Align"
        value={layer.textAlign}
        onChange={(v) => onChange({ textAlign: v })}
      />
      <LineHeightInput
        label="Line Height"
        value={layer.lineHeight}
        onChange={(v) => onChange({ lineHeight: v })}
        helpText="Leave blank to inherit the global line height."
      />
      <SectionHeader title="Content" />
      <div className="dnd-inspector-field">
        <button
          type="button"
          className="dnd-inspector-edit-btn"
          onClick={onEdit}
          disabled={!onEdit}
        >
          <Pencil size={14} /> Edit Text
        </button>
        <p className="dnd-inspector-hint">Or double-click the text on the canvas.</p>
      </div>
    </>
  );
}

function ImageLayerInspector({
  layer,
  onChange,
  onOpenPhotoPicker,
}: {
  layer: ImageLayer;
  onChange: (patch: Partial<ImageLayer>) => void;
  onOpenPhotoPicker?: (cb: (url: string, alt: string) => void) => void;
}) {
  return (
    <>
      <SectionHeader title="Image" />
      <TextInput
        label="Image URL"
        value={layer.src}
        onChange={(v) => onChange({ src: v })}
        placeholder="https://..."
        onBrowseImage={
          onOpenPhotoPicker
            ? () => onOpenPhotoPicker((url, alt) => onChange({ src: url, alt: alt || layer.alt }))
            : undefined
        }
      />
      <TextInput label="Alt Text" value={layer.alt} onChange={(v) => onChange({ alt: v })} placeholder="Image description" />
      {!layer.alt && (
        <div className="dnd-inspector-warning">
          <AlertTriangle size={12} />
          <span>Alt text is recommended for accessibility</span>
        </div>
      )}
      <TextInput label="Link URL" value={layer.href} onChange={(v) => onChange({ href: v })} placeholder="https://..." />
      {layer.href && (
        <SelectInput
          label="Link Target"
          value={layer.target}
          options={[
            { value: '_blank', label: 'New Tab (_blank)' },
            { value: '_self', label: 'Same Tab (_self)' },
          ]}
          onChange={(v) => onChange({ target: v as '_blank' | '_self' })}
        />
      )}
      <CheckboxInput label="Auto width (100%)" value={layer.autoWidth} onChange={(v) => onChange({ autoWidth: v })} />
      {!layer.autoWidth && (
        <NumberInput label="Width" value={layer.width} onChange={(v) => onChange({ width: v })} suffix="px" min={10} max={1200} />
      )}
      <CheckboxInput label="Retina (2x density)" value={layer.retina ?? false} onChange={(v) => onChange({ retina: v } as Partial<ImageLayer>)} />
      <AlignInput label="Alignment" value={layer.align} onChange={(v) => onChange({ align: v })} />
      <NumberInput label="Border Radius" value={layer.borderRadius} onChange={(v) => onChange({ borderRadius: v })} suffix="px" min={0} />
    </>
  );
}

function ButtonLayerInspector({
  layer,
  onChange,
  gs,
}: {
  layer: ButtonLayer;
  onChange: (patch: Partial<ButtonLayer>) => void;
  gs: GlobalStyles;
}) {
  return (
    <>
      <SectionHeader title="Button" />
      <TextInput label="Button Text" value={layer.text} onChange={(v) => onChange({ text: v })} />
      <TextInput label="Link URL" value={layer.href} onChange={(v) => onChange({ href: v })} placeholder="https://..." />
      <ColorField label="Button Color" variant="foreground" inheritedColor={gs.defaultButtonStyle.bgColor} value={layer.buttonBgColor} onChange={(v) => onChange({ buttonBgColor: v })} />
      <ColorField label="Text Color" variant="foreground" inheritedColor={gs.defaultButtonStyle.textColor} value={layer.textColor} onChange={(v) => onChange({ textColor: v })} />
      <SelectInput label="Font" value={layer.fontFamily} options={FONT_OPTIONS} onChange={(v) => onChange({ fontFamily: v })} />
      <TextInput label="Font Size" value={layer.fontSize} onChange={(v) => onChange({ fontSize: v })} placeholder="16px" />
      <NumberInput label="Border Radius" value={layer.borderRadius} onChange={(v) => onChange({ borderRadius: v })} suffix="px" min={0} />
      <CheckboxInput label="Full width" value={layer.fullWidth} onChange={(v) => onChange({ fullWidth: v })} />
      <AlignInput label="Alignment" value={layer.align} onChange={(v) => onChange({ align: v })} />
      <SpacingInput
        label="Button Padding"
        value={layer.buttonPadding}
        onChange={(v) => onChange({ buttonPadding: v })}
        helpText="Space inside the button, around its text. Larger values make a bigger button."
      />
    </>
  );
}

function DividerLayerInspector({
  layer,
  onChange,
}: {
  layer: DividerLayer;
  onChange: (patch: Partial<DividerLayer>) => void;
}) {
  return (
    <>
      <SectionHeader title="Divider" />
      <ColorInput label="Color" value={layer.borderColor} onChange={(v) => onChange({ borderColor: v })} />
      <SelectInput
        label="Style"
        value={layer.borderStyle}
        options={[
          { value: 'solid', label: 'Solid' },
          { value: 'dashed', label: 'Dashed' },
          { value: 'dotted', label: 'Dotted' },
        ]}
        onChange={(v) => onChange({ borderStyle: v as DividerLayer['borderStyle'] })}
      />
      <NumberInput label="Thickness" value={layer.borderWidth} onChange={(v) => onChange({ borderWidth: v })} suffix="px" min={1} max={10} />
      <TextInput label="Width" value={layer.dividerWidth} onChange={(v) => onChange({ dividerWidth: v })} placeholder="100%" />
      <AlignInput label="Alignment" value={layer.align} onChange={(v) => onChange({ align: v })} />
    </>
  );
}

function SpacerLayerInspector({
  layer,
  onChange,
}: {
  layer: SpacerLayer;
  onChange: (patch: Partial<SpacerLayer>) => void;
}) {
  return (
    <>
      <SectionHeader title="Spacer" />
      <NumberInput label="Height" value={layer.height} onChange={(v) => onChange({ height: v })} suffix="px" min={1} max={200} />
    </>
  );
}

function HtmlLayerInspector({
  layer,
  onChange,
  onOpenHtmlModal,
}: {
  layer: HtmlLayer;
  onChange: (patch: Partial<HtmlLayer>) => void;
  onOpenHtmlModal?: (initialHtml: string, cb: (html: string) => void) => void;
}) {
  return (
    <>
      <SectionHeader title="Custom HTML" />
      <div className="dnd-inspector-field">
        <button
          type="button"
          className="dnd-inspector-edit-html-btn"
          onClick={() => onOpenHtmlModal?.(layer.content, (html) => onChange({ content: html }))}
          disabled={!onOpenHtmlModal}
        >
          <Code2 size={14} /> Edit HTML
        </button>
      </div>
    </>
  );
}

const GAP_PRESETS: { label: string; value: number }[] = [
  { label: 'None', value: 0 },
  { label: 'S', value: 8 },
  { label: 'M', value: 16 },
  { label: 'L', value: 24 },
];

function GapControl({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <div className="dnd-preset-chips">
        {GAP_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            className={`dnd-chip${value === p.value ? ' active' : ''}`}
            onClick={() => onChange(p.value)}
          >
            {p.label}
          </button>
        ))}
        <input
          type="number"
          className="dnd-chip-number"
          value={value}
          min={0}
          max={64}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
          title="Custom gap in pixels"
          aria-label="Custom gap in pixels"
        />
      </div>
    </div>
  );
}

function ColumnsLayerInspector({
  layer,
  onChange,
}: {
  layer: ColumnsLayer;
  onChange: (patch: Partial<ColumnsLayer>) => void;
}) {
  return (
    <>
      <SectionHeader title="Columns" />
      <ColumnLayoutPicker
        value={layer.layout}
        onSelect={(l) => onChange(changeColumnsLayout(layer, l))}
      />
      <GapControl label="Column Gap" value={layer.columnsGap} onChange={(v) => onChange({ columnsGap: v })} />
      <p className="dnd-inspector-hint">Click a column in the canvas to style it individually.</p>
      <SectionHeader title="Mobile" />
      <CheckboxInput label="Stack columns on mobile" value={layer.stackOnMobile} onChange={(v) => onChange({ stackOnMobile: v })} />
      {layer.stackOnMobile && (
        <CheckboxInput
          label="Reverse order when stacked"
          value={layer.reverseStackOnMobile ?? false}
          onChange={(v) => onChange({ reverseStackOnMobile: v } as Partial<ColumnsLayer>)}
        />
      )}
    </>
  );
}

// A uniform border editor: one style/width/color, applied to whichever sides
// are toggled on (all four by default). A toggled-off side is stored as style
// 'none'. A hand-edited model whose sides differ in look (not just on/off)
// flattens to the first drawn side's look on the next edit here — this editor
// is deliberately uniform.
const BORDER_SIDE_KEYS = ['top', 'right', 'bottom', 'left'] as const;
type BorderSideKey = (typeof BORDER_SIDE_KEYS)[number];

function BorderInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BorderSides;
  onChange: (v: BorderSides) => void;
}) {
  const isDrawn = (s: BorderSide) => s.style !== 'none' && s.width > 0;
  const drawnKey = BORDER_SIDE_KEYS.find((k) => isDrawn(value[k]));
  // Style/width/color come from the first drawn side, so a model with only a
  // bottom border opens showing that border's settings.
  const master = drawnKey ? value[drawnKey] : value.top;
  const off = !drawnKey;
  // With the border off, the toggles reset to all-on so re-enabling the style
  // draws a full box again.
  const enabled: Record<BorderSideKey, boolean> = off
    ? { top: true, right: true, bottom: true, left: true }
    : { top: isDrawn(value.top), right: isDrawn(value.right), bottom: isDrawn(value.bottom), left: isDrawn(value.left) };

  const apply = (patch: Partial<BorderSide>, sides: Record<BorderSideKey, boolean> = enabled) => {
    const next: BorderSide = { ...master, ...patch };
    const mk = (on: boolean): BorderSide =>
      on ? { ...next } : { width: 0, style: 'none', color: next.color };
    // Spread value first so the box-level radius survives side rewrites.
    onChange({ ...value, top: mk(sides.top), right: mk(sides.right), bottom: mk(sides.bottom), left: mk(sides.left) });
  };

  return (
    <>
      <div className="dnd-inspector-field">
        <label>{label}</label>
        <div className="dnd-border-row">
          <select
            value={off ? 'none' : master.style}
            onChange={(e) => {
              const style = e.target.value as BorderSide['style'];
              // Turning a border on with width 0 would draw nothing — give it
              // a visible width so the picked style shows up immediately.
              apply(style !== 'none' && master.width === 0 ? { style, width: 1 } : { style });
            }}
          >
            <option value="none">None</option>
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
          <input
            type="number"
            min={0}
            max={20}
            value={master.width}
            onChange={(e) => apply({ width: Math.max(0, Number(e.target.value)) })}
            title="Border width in pixels"
            aria-label="Border width in pixels"
            disabled={off}
          />
        </div>
      </div>
      {/* Side toggles and color only appear while a border is drawn — with
          style "none" there is nothing to place or color. */}
      {!off && (
        <>
          <NumberInput
            label="Border Radius"
            value={value.radius ?? 0}
            onChange={(v) => onChange({ ...value, radius: v })}
            suffix="px"
            min={0}
          />
          <div className="dnd-inspector-field">
            <label>Border Sides</label>
            <div className="dnd-align-group">
              {BORDER_SIDE_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={enabled[k] ? 'active' : undefined}
                  aria-pressed={enabled[k]}
                  title={`${enabled[k] ? 'Hide' : 'Show'} the ${k} border`}
                  onClick={() => apply({}, { ...enabled, [k]: !enabled[k] })}
                >
                  {k.charAt(0).toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <ColorInput
            label={`${label} Color`}
            value={master.color || '#000000'}
            onChange={(v) => apply({ color: v })}
          />
        </>
      )}
    </>
  );
}

function ColumnInspector({
  column,
  onChange,
}: {
  column: Column;
  onChange: (patch: Partial<Column>) => void;
}) {
  return (
    <>
      <SectionHeader title="Column" />
      <ColorField label="Background Color" variant="background" value={column.bgColor} onChange={(v) => onChange({ bgColor: v })} />
      <SpacingInput
        label="Padding"
        value={column.padding}
        onChange={(v) => onChange({ padding: v })}
        helpText="Space inside this column, around its content."
      />
      <SelectInput
        label="Vertical Align"
        value={column.valign}
        options={[
          { value: 'top', label: 'Top' },
          { value: 'middle', label: 'Middle' },
          { value: 'bottom', label: 'Bottom' },
        ]}
        onChange={(v) => onChange({ valign: v as Column['valign'] })}
      />
      <BorderInput label="Border" value={column.border} onChange={(v) => onChange({ border: v })} />
    </>
  );
}

// Shared social controls used by both the Social layer and Social element.
interface SocialFieldValues {
  style: 'icons-only' | 'icons-text' | 'text-only';
  iconSize: number;
  align: 'left' | 'center' | 'right';
  spacing: number;
  lineHeight?: string;
  iconColor?: string;
  links: SocialLink[];
  logoSetId?: number | null;
  logos?: SocialLogos;
}

function SocialFields({
  value,
  onChange,
  inheritedColor,
}: {
  value: SocialFieldValues;
  onChange: (patch: Partial<SocialFieldValues>) => void;
  inheritedColor?: string;
}) {
  const { data: logoSets } = useSocialLogoSets();
  const hasSets = !!logoSets?.length;

  const updateLink = (idx: number, patch: Partial<SocialLink>) => {
    const links = value.links.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    onChange({ links });
  };

  const applyLogoSet = (id: number) => {
    const set = logoSets?.find((s) => s.socialLogoSetID === id);
    if (!set) return;
    // Snapshot the set's per-platform PNG URLs so the compiler/preview render
    // without a DB lookup and stay stable if the set is later edited.
    onChange({ logoSetId: id, logos: logosFromSet(set) });
  };

  // A logo set is always selected: the built-in glyphs are inline SVG, which
  // Outlook/Gmail strip, so there is no "default/none" option. Auto-pick the
  // first set for blocks without a valid one (new blocks, or ones saved before
  // logo sets existed — this also migrates their SVG icons to email-safe PNGs).
  useEffect(() => {
    if (!logoSets?.length) return;
    const valid = value.logoSetId != null && logoSets.some((s) => s.socialLogoSetID === value.logoSetId);
    if (!valid) applyLogoSet(logoSets[0].socialLogoSetID);
    // applyLogoSet/onChange are stable enough for this guard; re-run only on set/selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoSets, value.logoSetId]);

  const showText = value.style !== 'icons-only';

  return (
    <>
      <SectionHeader title="Social Links" />
      {hasSets ? (
        <SelectInput
          label="Icon Style"
          value={value.logoSetId != null ? String(value.logoSetId) : ''}
          options={logoSets!.map((s) => ({ value: String(s.socialLogoSetID), label: s.name }))}
          onChange={(v) => applyLogoSet(Number(v))}
        />
      ) : (
        <p style={{ fontSize: 11, color: '#c0392b', margin: '0 0 8px', lineHeight: 1.4 }}>
          No social logo sets are configured. Icons fall back to non–email-safe
          glyphs until a set is added in settings.
        </p>
      )}
      <SelectInput
        label="Style"
        value={value.style}
        options={[
          { value: 'icons-only', label: 'Icons Only' },
          { value: 'icons-text', label: 'Icons + Text' },
          { value: 'text-only', label: 'Text Only' },
        ]}
        onChange={(v) => onChange({ style: v as SocialFieldValues['style'] })}
      />
      <NumberInput label="Icon Size" value={value.iconSize} onChange={(v) => onChange({ iconSize: v })} suffix="px" min={16} max={64} />
      {/* PNG icons are pre-colored, so the color control only affects the text
          labels — show it only when a style displays them. */}
      {showText && (
        <ColorField
          label="Text Color"
          variant="foreground"
          inheritedColor={inheritedColor}
          value={value.iconColor || ''}
          onChange={(v) => onChange({ iconColor: v })}
        />
      )}
      <AlignInput label="Alignment" value={value.align} onChange={(v) => onChange({ align: v })} />
      <NumberInput label="Spacing" value={value.spacing} onChange={(v) => onChange({ spacing: v })} suffix="px" min={0} max={24} />
      <LineHeightInput
        label="Line Height"
        value={value.lineHeight}
        onChange={(v) => onChange({ lineHeight: v })}
        helpText="Vertical space per row. Set higher than the icon size to keep rows apart when icons wrap."
      />
      <SectionHeader title="Platforms" />
      {value.links.map((link, idx) => {
        const variable = SOCIAL_PLATFORM_VARIABLES[link.platform];
        return (
          <div key={link.platform} className="dnd-inspector-field" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <input
              type="checkbox"
              checked={link.enabled}
              onChange={(e) => updateLink(idx, { enabled: e.target.checked })}
            />
            <span style={{ width: 70, fontSize: 12, textTransform: 'capitalize' }}>{link.platform}</span>
            <input
              type="text"
              value={link.url}
              onChange={(e) => updateLink(idx, { url: e.target.value })}
              placeholder={`${link.platform} URL`}
              style={{ flex: 1, padding: '4px 6px', border: '1px solid #ddd', borderRadius: 3, fontSize: 11 }}
              disabled={!link.enabled}
            />
            {variable && (
              <button
                type="button"
                className="dnd-social-var-btn"
                title={`Use facility ${link.platform} link (${variable})`}
                aria-label={`Use facility ${link.platform} link`}
                onClick={() => updateLink(idx, { url: variable })}
                disabled={!link.enabled}
              >
                <Braces size={13} />
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

function SocialLayerInspector({
  layer,
  onChange,
  inheritedColor,
}: {
  layer: SocialLayer;
  onChange: (patch: Partial<SocialLayer>) => void;
  inheritedColor?: string;
}) {
  return <SocialFields value={layer} onChange={onChange} inheritedColor={inheritedColor} />;
}

function VideoLayerInspector({
  layer,
  onChange,
}: {
  layer: VideoLayer;
  onChange: (patch: Partial<VideoLayer>) => void;
}) {
  return (
    <>
      <SectionHeader title="Video" />
      <TextInput label="Video URL" value={layer.videoUrl} onChange={(v) => onChange({ videoUrl: v })} placeholder="https://youtube.com/..." />
      <TextInput label="Thumbnail URL" value={layer.thumbnailSrc} onChange={(v) => onChange({ thumbnailSrc: v })} placeholder="https://..." />
      <TextInput label="Alt Text" value={layer.alt} onChange={(v) => onChange({ alt: v })} placeholder="Play video" />
      <ColorInput label="Play Button Color" value={layer.playButtonColor} onChange={(v) => onChange({ playButtonColor: v })} />
    </>
  );
}

function CountdownLayerInspector({
  layer,
  onChange,
}: {
  layer: CountdownLayer;
  onChange: (patch: Partial<CountdownLayer>) => void;
}) {
  return (
    <>
      <SectionHeader title="Countdown" />
      <div className="dnd-inspector-field">
        <label>Target Date</label>
        <input
          type="datetime-local"
          value={layer.targetDate ? new Date(layer.targetDate).toISOString().slice(0, 16) : ''}
          onChange={(e) => onChange({ targetDate: new Date(e.target.value).toISOString() })}
          style={{ width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12 }}
        />
      </div>
      <TextInput label="Timezone" value={layer.timezone} onChange={(v) => onChange({ timezone: v })} />
      <SelectInput
        label="Style"
        value={layer.countdownStyle}
        options={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
          { value: 'custom', label: 'Custom' },
        ]}
        onChange={(v) => onChange({ countdownStyle: v as CountdownLayer['countdownStyle'] })}
      />
      {layer.countdownStyle === 'custom' && (
        <>
          <ColorInput label="Label Color" value={layer.labelColor} onChange={(v) => onChange({ labelColor: v })} />
          <ColorInput label="Digit Color" value={layer.digitColor} onChange={(v) => onChange({ digitColor: v })} />
          <ColorInput label="Digit Background" value={layer.digitBgColor} onChange={(v) => onChange({ digitBgColor: v })} />
        </>
      )}
      <CheckboxInput label="Show labels" value={layer.showLabels} onChange={(v) => onChange({ showLabels: v })} />
      {layer.showLabels && (
        <>
          <TextInput label="Days Label" value={layer.labels.days} onChange={(v) => onChange({ labels: { ...layer.labels, days: v } })} />
          <TextInput label="Hours Label" value={layer.labels.hours} onChange={(v) => onChange({ labels: { ...layer.labels, hours: v } })} />
          <TextInput label="Minutes Label" value={layer.labels.minutes} onChange={(v) => onChange({ labels: { ...layer.labels, minutes: v } })} />
          <TextInput label="Seconds Label" value={layer.labels.seconds} onChange={(v) => onChange({ labels: { ...layer.labels, seconds: v } })} />
        </>
      )}
    </>
  );
}

// ---- Common layer settings ------------------------------------------------

function CommonLayerSettings({
  layer,
  onChange,
  onOpenPhotoPicker,
  gs,
}: {
  layer: Layer;
  onChange: (patch: Partial<Layer>) => void;
  onOpenPhotoPicker?: (cb: (url: string, alt: string) => void) => void;
  gs: GlobalStyles;
}) {
  return (
    <>
      <SectionHeader title="Layer Settings" />
      <SpacingInput label="Padding" value={layer.padding} onChange={(v) => onChange({ padding: v })} />
      {/* A spacer has no box of its own — height is its only dimension. */}
      {layer.type !== 'spacer' && (
        <>
          <BorderInput label="Border" value={layer.border} onChange={(v) => onChange({ border: v })} />
          <SpacingInput
            label="Outer Spacing"
            value={layer.margin ?? { top: 0, right: 0, bottom: 0, left: 0 }}
            onChange={(v) => onChange({ margin: v })}
            helpText="Space outside the block and its border, between it and neighboring blocks."
          />
        </>
      )}
      {/* Social layers use the dedicated "Icon Color" control above instead. */}
      {layer.type !== 'social' && (
        <ColorField label="Font Color" variant="foreground" inheritedColor={gs.textColor} value={layer.textColor} onChange={(v) => onChange({ textColor: v })} />
      )}
      <ColorField label="Background Color" variant="background" value={layer.bgColor} onChange={(v) => onChange({ bgColor: v })} />
      <SectionHeader title="Background Image" />
      <div className="dnd-inspector-field">
        <label>Image URL</label>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="text"
            value={layer.bgImage}
            onChange={(e) => onChange({ bgImage: e.target.value })}
            placeholder="https://..."
            style={{ flex: 1 }}
          />
          {onOpenPhotoPicker && (
            <button
              className="dnd-inspector-browse-btn"
              onClick={() => onOpenPhotoPicker((url) => onChange({ bgImage: url }))}
              title="Browse photos"
            >
              Browse
            </button>
          )}
        </div>
      </div>
      {layer.bgImage && (
        <>
          <SelectInput
            label="Size"
            value={layer.bgSize}
            options={[
              { value: 'cover', label: 'Cover' },
              { value: 'contain', label: 'Contain' },
              { value: 'auto', label: 'Auto' },
            ]}
            onChange={(v) => onChange({ bgSize: v as 'cover' | 'contain' | 'auto' })}
          />
          <SelectInput
            label="Repeat"
            value={layer.bgRepeat}
            options={[
              { value: 'no-repeat', label: 'No Repeat' },
              { value: 'repeat', label: 'Repeat' },
              { value: 'repeat-x', label: 'Repeat X' },
              { value: 'repeat-y', label: 'Repeat Y' },
            ]}
            onChange={(v) => onChange({ bgRepeat: v as 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y' })}
          />
          <TextInput label="Position" value={layer.bgPosition} onChange={(v) => onChange({ bgPosition: v })} placeholder="center center" />
        </>
      )}
      <SectionHeader title="Visibility" />
      <CheckboxInput label="Hide on mobile" value={layer.hideOnMobile} onChange={(v) => onChange({ hideOnMobile: v })} />
      <CheckboxInput label="Hide on desktop" value={layer.hideOnDesktop} onChange={(v) => onChange({ hideOnDesktop: v })} />
      {(layer.type === 'text' || layer.type === 'image') && (
        <>
          <SectionHeader title="Content Lock" />
          <CheckboxInput
            label="Lock content (restricted editors cannot modify)"
            value={layer.locked}
            onChange={(v) => onChange({ locked: v })}
          />
        </>
      )}
    </>
  );
}

// ---- Breadcrumb trail (Layer › Column › Element) --------------------------
//
// Lets the user jump to any container in the selection hierarchy — essential
// when a container has no padding and nothing clickable in the canvas.

function InspectorBreadcrumbs({
  model,
  selection,
  onSelectLayer,
  onSelectColumn,
  onSelectElement,
}: {
  model: DndEmailModel;
  selection: SelectionState;
  onSelectLayer: (id: string | null) => void;
  onSelectColumn: (layerId: string, columnId: string) => void;
  onSelectElement: (layerId: string, columnId: string, elementId: string) => void;
}) {
  if (!selection.layerId) return null;
  const layer = findLayerById(model, selection.layerId);
  if (!layer) return null;

  const isColumns = layer.type === 'columns';
  const showColumn = isColumns && !!selection.columnId;
  const showElement = !!selection.elementId;

  const crumbs: { key: string; label: string; active: boolean; onClick?: () => void }[] = [];

  crumbs.push({
    key: 'layer',
    label: isColumns ? 'Columns' : layer.label || layer.type.charAt(0).toUpperCase() + layer.type.slice(1),
    active: !showColumn && !showElement,
    onClick: () => onSelectLayer(layer.id),
  });

  if (showColumn) {
    const cols = (layer as ColumnsLayer).columns;
    const colIdx = cols.findIndex((c) => c.id === selection.columnId);
    crumbs.push({
      key: 'column',
      label: colIdx >= 0 ? `Column ${colIdx + 1}` : 'Column',
      active: !showElement,
      onClick: () => onSelectColumn(layer.id, selection.columnId as string),
    });
  }

  if (showElement) {
    const info = findElementById(model, selection.elementId as string);
    const type = info?.element.type ?? 'element';
    crumbs.push({
      key: 'element',
      label: type.charAt(0).toUpperCase() + type.slice(1),
      active: true,
      onClick:
        showColumn && selection.columnId
          ? () => onSelectElement(layer.id, selection.columnId as string, selection.elementId as string)
          : undefined,
    });
  }

  return (
    <div className="dnd-breadcrumbs">
      {crumbs.map((c, i) => (
        <React.Fragment key={c.key}>
          {i > 0 && <span className="dnd-breadcrumb-sep" aria-hidden="true">›</span>}
          {c.active ? (
            <span className="dnd-breadcrumb active" aria-current="true">{c.label}</span>
          ) : (
            <button type="button" className="dnd-breadcrumb" onClick={c.onClick}>{c.label}</button>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ---- Main Inspector -------------------------------------------------------

interface DndInspectorProps {
  selection: SelectionState;
  model: DndEmailModel;
  onUpdateLayer: (id: string, patch: Partial<Layer>) => void;
  onUpdateElement: (
    layerId: string,
    columnId: string,
    elementId: string,
    patch: Partial<Element>,
  ) => void;
  onUpdateColumn: (
    layerId: string,
    columnId: string,
    patch: Partial<Column>,
  ) => void;
  onSelectLayer: (id: string | null) => void;
  onSelectColumn: (layerId: string, columnId: string) => void;
  onSelectElement: (layerId: string, columnId: string, elementId: string) => void;
  onOpenPhotoPicker?: (cb: (url: string, alt: string) => void) => void;
  onOpenHtmlModal?: (initialHtml: string, cb: (html: string) => void) => void;
  /**
   * Open the canvas's inline WYSIWYG on a text node. Inline editing is canvas
   * state, so the "Edit Text" buttons here ask for it rather than owning it.
   */
  onRequestInlineEdit?: (layerId: string, columnId?: string, elementId?: string) => void;
}

// Editor-access lock, shown at the top of the inspector for text & image blocks
// only. "Unlocked" (green) means downstream HTML-editor users may edit this
// block; "Locked" protects it. Mirrors the FtpEditor lock semantics.
function LockToggle({ locked, onToggle }: { locked: boolean; onToggle: () => void }) {
  const unlocked = !locked;
  return (
    <div className="dnd-lock-toggle-wrap">
      <button
        type="button"
        className={`dnd-lock-toggle${unlocked ? ' unlocked' : ' locked'}`}
        onClick={onToggle}
        aria-pressed={unlocked}
        title={unlocked ? 'Editors can edit this block — click to lock' : 'Locked — click to let editors edit this block'}
      >
        <span className="dnd-lock-toggle-face">
          {unlocked ? <Unlock size={14} /> : <Lock size={14} />}
          <span className="dnd-lock-toggle-label">{unlocked ? 'Unlocked for editors' : 'Locked for editors'}</span>
        </span>
        <span className="dnd-lock-switch" aria-hidden="true"><span className="dnd-lock-switch-knob" /></span>
      </button>
    </div>
  );
}

export function DndInspector({
  selection,
  model,
  onUpdateLayer,
  onUpdateElement,
  onUpdateColumn,
  onSelectLayer,
  onSelectColumn,
  onSelectElement,
  onOpenPhotoPicker,
  onOpenHtmlModal,
  onRequestInlineEdit,
}: DndInspectorProps) {
  // Selected element (inside a column)
  const elementInfo = useMemo(() => {
    if (!selection.elementId) return null;
    return findElementById(model, selection.elementId);
  }, [model, selection.elementId]);

  // Selected layer
  const layer = useMemo(() => {
    if (!selection.layerId) return null;
    return findLayerById(model, selection.layerId);
  }, [model, selection.layerId]);

  // Selected column (a column cell, with no element selected inside it)
  const columnInfo = useMemo(() => {
    if (!selection.columnId || selection.elementId) return null;
    if (!layer || layer.type !== 'columns') return null;
    const column = (layer as ColumnsLayer).columns.find((c) => c.id === selection.columnId);
    return column ? { column } : null;
  }, [layer, selection.columnId, selection.elementId]);

  const handleLayerChange = useCallback(
    (patch: Partial<Layer>) => {
      if (selection.layerId) onUpdateLayer(selection.layerId, patch);
    },
    [selection.layerId, onUpdateLayer],
  );

  const handleColumnChange = useCallback(
    (patch: Partial<Column>) => {
      if (selection.layerId && selection.columnId) {
        onUpdateColumn(selection.layerId, selection.columnId, patch);
      }
    },
    [selection.layerId, selection.columnId, onUpdateColumn],
  );

  const handleElementChange = useCallback(
    (patch: Partial<Element>) => {
      if (selection.layerId && selection.columnId && selection.elementId) {
        onUpdateElement(selection.layerId, selection.columnId, selection.elementId, patch);
      }
    },
    [selection, onUpdateElement],
  );

  // Undefined (rather than a no-op) when editing can't be requested, so the
  // button renders disabled instead of looking clickable and doing nothing.
  const handleEditElement = useMemo(() => {
    if (!onRequestInlineEdit || !selection.layerId || !selection.columnId || !selection.elementId) return undefined;
    const { layerId, columnId, elementId } = selection;
    return () => onRequestInlineEdit(layerId, columnId, elementId);
  }, [onRequestInlineEdit, selection]);

  const breadcrumbs = (
    <InspectorBreadcrumbs
      model={model}
      selection={selection}
      onSelectLayer={onSelectLayer}
      onSelectColumn={onSelectColumn}
      onSelectElement={onSelectElement}
    />
  );

  // ---- Element inspector ----

  if (elementInfo) {
    const el = elementInfo.element;
    return (
      <div className="dnd-inspector">
        {breadcrumbs}
        <div className="dnd-inspector-scroll">
          {(el.type === 'text' || el.type === 'image') && (
            <LockToggle
              locked={el.locked ?? true}
              onToggle={() => handleElementChange({ locked: !(el.locked ?? true) })}
            />
          )}
          {el.type === 'text' && (
            <>
              <AlignInput label="Text Align" value={(el as import('./dnd-model').TextElement).textAlign} onChange={(v) => handleElementChange({ textAlign: v } as Partial<import('./dnd-model').TextElement>)} />
              <LineHeightInput
                label="Line Height"
                value={(el as import('./dnd-model').TextElement).lineHeight}
                onChange={(v) => handleElementChange({ lineHeight: v } as Partial<import('./dnd-model').TextElement>)}
                helpText="Leave blank to inherit the global line height."
              />
              <div className="dnd-inspector-field">
                <button
                  type="button"
                  className="dnd-inspector-edit-btn"
                  onClick={handleEditElement}
                  disabled={!handleEditElement}
                >
                  <Pencil size={14} /> Edit Text
                </button>
                <p className="dnd-inspector-hint">Or double-click the text on the canvas.</p>
              </div>
            </>
          )}
          {el.type === 'image' && (() => {
            const img = el as import('./dnd-model').ImageElement;
            return (
              <>
                <TextInput
                  label="Image URL"
                  value={img.src}
                  onChange={(v) => handleElementChange({ src: v })}
                  onBrowseImage={
                    onOpenPhotoPicker
                      ? () => onOpenPhotoPicker((url, alt) => handleElementChange({ src: url, alt: alt || img.alt }))
                      : undefined
                  }
                />
                <TextInput label="Alt Text" value={img.alt} onChange={(v) => handleElementChange({ alt: v })} />
                {!img.alt && (
                  <div className="dnd-inspector-warning">
                    <AlertTriangle size={12} />
                    <span>Alt text is recommended for accessibility</span>
                  </div>
                )}
                <TextInput label="Link URL" value={img.href} onChange={(v) => handleElementChange({ href: v })} />
                {img.href && (
                  <SelectInput
                    label="Link Target"
                    value={img.target}
                    options={[
                      { value: '_blank', label: 'New Tab (_blank)' },
                      { value: '_self', label: 'Same Tab (_self)' },
                    ]}
                    onChange={(v) => handleElementChange({ target: v as '_blank' | '_self' })}
                  />
                )}
                <CheckboxInput label="Auto width (100%)" value={img.autoWidth} onChange={(v) => handleElementChange({ autoWidth: v })} />
                {!img.autoWidth && (
                  <NumberInput label="Width" value={img.width} onChange={(v) => handleElementChange({ width: v })} suffix="px" min={10} max={1200} />
                )}
                <CheckboxInput label="Retina (2x density)" value={img.retina ?? false} onChange={(v) => handleElementChange({ retina: v })} />
                <AlignInput label="Alignment" value={img.align} onChange={(v) => handleElementChange({ align: v })} />
                <NumberInput label="Border Radius" value={img.borderRadius} onChange={(v) => handleElementChange({ borderRadius: v })} suffix="px" min={0} />
              </>
            );
          })()}
          {el.type === 'button' && (
            <>
              <TextInput label="Button Text" value={(el as import('./dnd-model').ButtonElement).text} onChange={(v) => handleElementChange({ text: v })} />
              <TextInput label="Link URL" value={(el as import('./dnd-model').ButtonElement).href} onChange={(v) => handleElementChange({ href: v })} />
              <ColorField label="Button Color" variant="foreground" inheritedColor={model.globalStyles.defaultButtonStyle.bgColor} value={(el as import('./dnd-model').ButtonElement).bgColor} onChange={(v) => handleElementChange({ bgColor: v })} />
              <ColorField label="Text Color" variant="foreground" inheritedColor={model.globalStyles.defaultButtonStyle.textColor} value={(el as import('./dnd-model').ButtonElement).textColor} onChange={(v) => handleElementChange({ textColor: v })} />
            </>
          )}
          {el.type === 'divider' && (
            <>
              <ColorInput label="Color" value={(el as import('./dnd-model').DividerElement).borderColor} onChange={(v) => handleElementChange({ borderColor: v })} />
              <NumberInput label="Thickness" value={(el as import('./dnd-model').DividerElement).borderWidth} onChange={(v) => handleElementChange({ borderWidth: v })} suffix="px" min={1} />
            </>
          )}
          {el.type === 'spacer' && (
            <NumberInput label="Height" value={(el as import('./dnd-model').SpacerElement).height} onChange={(v) => handleElementChange({ height: v })} suffix="px" min={1} />
          )}
          {el.type === 'html' && (
            <div className="dnd-inspector-field">
              <button
                type="button"
                className="dnd-inspector-edit-html-btn"
                onClick={() => onOpenHtmlModal?.((el as import('./dnd-model').HtmlElement).content, (html) => handleElementChange({ content: html }))}
                disabled={!onOpenHtmlModal}
              >
                <Code2 size={14} /> Edit HTML
              </button>
            </div>
          )}
          {el.type === 'social' && (
            <SocialFields
              value={el as import('./dnd-model').SocialElement}
              onChange={(patch) => handleElementChange(patch as Partial<Element>)}
              inheritedColor={model.globalStyles.textColor}
            />
          )}
          <SectionHeader title="Element Settings" />
          <SpacingInput label="Padding" value={el.padding} onChange={(v) => handleElementChange({ padding: v })} />
          {el.type !== 'spacer' && (
            <>
              <BorderInput
                label="Border"
                value={el.border ?? defaultBorderSides()}
                onChange={(v) => handleElementChange({ border: v })}
              />
              <SpacingInput
                label="Outer Spacing"
                value={el.margin ?? { top: 0, right: 0, bottom: 0, left: 0 }}
                onChange={(v) => handleElementChange({ margin: v })}
                helpText="Space outside the block and its border, between it and neighboring blocks."
              />
            </>
          )}
          <CheckboxInput label="Hide on mobile" value={el.hideOnMobile} onChange={(v) => handleElementChange({ hideOnMobile: v })} />
          <CheckboxInput label="Hide on desktop" value={el.hideOnDesktop} onChange={(v) => handleElementChange({ hideOnDesktop: v })} />
        </div>
      </div>
    );
  }

  // ---- Column inspector ----

  if (columnInfo) {
    return (
      <div className="dnd-inspector">
        {breadcrumbs}
        <div className="dnd-inspector-scroll">
          <ColumnInspector column={columnInfo.column} onChange={handleColumnChange} />
        </div>
      </div>
    );
  }

  // ---- Layer inspector ----

  if (!layer) {
    return (
      <div className="dnd-inspector">
        <div className="dnd-inspector-empty">
          <MousePointer size={28} />
          <p>Select a layer to edit its properties</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dnd-inspector">
      {breadcrumbs}
      <div className="dnd-inspector-scroll">
        {(layer.type === 'text' || layer.type === 'image') && (
          <LockToggle
            locked={layer.locked}
            onToggle={() => handleLayerChange({ locked: !layer.locked })}
          />
        )}
        {layer.type === 'text' && (
          <TextLayerInspector
            layer={layer}
            onChange={handleLayerChange}
            onEdit={onRequestInlineEdit ? () => onRequestInlineEdit(layer.id) : undefined}
          />
        )}
        {layer.type === 'image' && <ImageLayerInspector layer={layer} onChange={handleLayerChange} onOpenPhotoPicker={onOpenPhotoPicker} />}
        {layer.type === 'button' && <ButtonLayerInspector layer={layer} onChange={handleLayerChange} gs={model.globalStyles} />}
        {layer.type === 'divider' && <DividerLayerInspector layer={layer} onChange={handleLayerChange} />}
        {layer.type === 'spacer' && <SpacerLayerInspector layer={layer} onChange={handleLayerChange} />}
        {layer.type === 'html' && <HtmlLayerInspector layer={layer} onChange={handleLayerChange} onOpenHtmlModal={onOpenHtmlModal} />}
        {layer.type === 'columns' && <ColumnsLayerInspector layer={layer as ColumnsLayer} onChange={handleLayerChange} />}
        {layer.type === 'social' && <SocialLayerInspector layer={layer as SocialLayer} onChange={handleLayerChange} inheritedColor={model.globalStyles.textColor} />}
        {layer.type === 'video' && <VideoLayerInspector layer={layer as VideoLayer} onChange={handleLayerChange} />}
        {layer.type === 'countdown' && <CountdownLayerInspector layer={layer as CountdownLayer} onChange={handleLayerChange} />}
        <CommonLayerSettings layer={layer} onChange={handleLayerChange} onOpenPhotoPicker={onOpenPhotoPicker} gs={model.globalStyles} />
      </div>
    </div>
  );
}
